import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { CartItem, LicenseType, Product } from "../types/store";

const CART_KEY = "modelerman.store.cart";

interface AddToCartPayload {
    product: Product;
    quantity?: number;
    license?: LicenseType;
    isDigital?: boolean;
}

interface CartContextValue {
    items: CartItem[];
    isOpen: boolean;
    subtotal: number;
    total: number;
    hasPhysicalItem: boolean;
    openDrawer: () => void;
    closeDrawer: () => void;
    addItem: (payload: AddToCartPayload) => void;
    removeItem: (itemId: string) => void;
    updateQuantity: (itemId: string, quantity: number) => void;
    updateLicense: (itemId: string, license: LicenseType) => void;
    clearCart: () => void;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

type ProviderProps = {
    children: React.ReactNode;
};

const buildShortInfo = (product: Product): string | undefined => {
    const { meta } = product;
    if (!meta) return undefined;
    const tokens: string[] = [];
    if (meta.texturesCount) {
        tokens.push(`${meta.texturesCount} textures`);
    }
    if (meta.stlCount) {
        tokens.push(`${meta.stlCount} STL files`);
    }
    if (meta.resolution) {
        tokens.push(meta.resolution);
    }
    if (meta.printTimeEstimateHours) {
        tokens.push(`${meta.printTimeEstimateHours}h print time`);
    }
    return tokens.join(" • ") || undefined;
};

const deriveDigitalFlag = (product: Product, explicit?: boolean): boolean => {
    if (explicit !== undefined) return explicit;
    return product.type === "TEXTURE" || product.type === "STL";
};

export const CartProvider: React.FC<ProviderProps> = ({ children }: ProviderProps) => {
    const [items, setItems] = useState<CartItem[]>([]);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        const cached = window.localStorage.getItem(CART_KEY);
        if (!cached) return;
        try {
            const parsed = JSON.parse(cached) as CartItem[];
            setItems(parsed);
        } catch (error) {
            console.warn("Failed to parse cart", error);
            window.localStorage.removeItem(CART_KEY);
        }
    }, []);

    useEffect(() => {
        window.localStorage.setItem(CART_KEY, JSON.stringify(items));
    }, [items]);

    const addItem = ({ product, quantity = 1, license, isDigital }: AddToCartPayload) => {
        setItems((current: CartItem[]) => {
            const existing = current.find(
                (item: CartItem) => item.productId === product.id && item.license === license
            );
            if (existing) {
                return current.map((item: CartItem) =>
                    item.id === existing.id ? { ...item, quantity: item.quantity + quantity } : item
                );
            }
            const newItem: CartItem = {
                id: crypto.randomUUID(),
                productId: product.id,
                productType: product.type,
                name: product.name,
                thumbnailUrl: product.thumbnailUrl,
                unitPrice: product.price,
                quantity,
                license,
                isDigital: deriveDigitalFlag(product, isDigital),
                shortInfo: buildShortInfo(product)
            };
            return [...current, newItem];
        });
        setIsOpen(true);
    };

    const removeItem = (itemId: string) => {
        setItems((current: CartItem[]) => current.filter((item: CartItem) => item.id !== itemId));
    };

    const updateQuantity = (itemId: string, quantity: number) => {
        setItems((current: CartItem[]) =>
            current.map((item: CartItem) =>
                item.id === itemId ? { ...item, quantity: Math.max(1, quantity) } : item
            )
        );
    };

    const updateLicense = (itemId: string, license: LicenseType) => {
        setItems((current: CartItem[]) =>
            current.map((item: CartItem) => (item.id === itemId ? { ...item, license } : item))
        );
    };

    const clearCart = () => setItems([]);

    const subtotal = useMemo(
        () => items.reduce((sum: number, item: CartItem) => sum + item.unitPrice * item.quantity, 0),
        [items]
    );
    const hasPhysicalItem = items.some((item: CartItem) => !item.isDigital);
    const total = subtotal; // placeholder for VAT/shipping logic

    const value = useMemo<CartContextValue>(
        () => ({
            items,
            isOpen,
            subtotal,
            total,
            hasPhysicalItem,
            openDrawer: () => setIsOpen(true),
            closeDrawer: () => setIsOpen(false),
            addItem,
            removeItem,
            updateQuantity,
            updateLicense,
            clearCart
        }),
        [items, isOpen, subtotal, total, hasPhysicalItem]
    );

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) {
        throw new Error("useCart must be used within a CartProvider");
    }
    return ctx;
};
